import { get, post } from './api';

/**
 * Web Push enrolment, and the iOS special case that dominates it.
 *
 * Safari will only deliver Web Push to a PWA that has been added to the home
 * screen. Not "works better if" — `Notification` and `PushManager` are simply
 * absent in a normal Safari tab. So on iOS the notification flow is not a
 * permission prompt, it is a walkthrough: Share → Add to Home Screen → open
 * the installed app → *then* ask permission.
 *
 * Getting this wrong is the single most likely way for this app to fail
 * quietly at a restaurant: the manager posts "don't open the walk-in", three
 * people on iPhones never get notified, and nobody understands why.
 */

export type PushStatus =
  | 'unsupported'
  /** iOS Safari in a browser tab: needs Add to Home Screen first. */
  | 'needs-install'
  | 'prompt'
  | 'granted'
  | 'denied';

export function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * True when this is iOS *and* not Safari.
 *
 * Every iOS browser is WebKit underneath, but only Safari can Add to Home
 * Screen in a way that produces a push-capable web app. Chrome, Firefox and
 * friends identify themselves in the UA (CriOS, FxiOS, EdgiOS, OPiOS…) — and
 * telling a Chrome user to "tap Share in Safari" is worse than saying nothing,
 * because the Share menu they are looking at has no such option.
 */
export function isIosNonSafari(): boolean {
  if (!isIos()) return false;
  return /CriOS|FxiOS|EdgiOS|OPiOS|mercury|GSA/i.test(navigator.userAgent);
}

/** True when running as an installed PWA rather than in a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's non-standard property, which is the only reliable signal on iOS.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function pushStatus(): PushStatus {
  if (isIos() && !isStandalone()) return 'needs-install';
  if (
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return 'unsupported';
  }
  const permission = Notification.permission;
  if (permission === 'granted') return 'granted';
  if (permission === 'denied') return 'denied';
  return 'prompt';
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

    // Force an update check on every launch.
    //
    // An installed home-screen app keeps its service worker indefinitely; the
    // browser only re-fetches sw.js on its own schedule, which on iOS can mean
    // a very long time. Without this, shipping a fix *to the service worker*
    // — the code that handles push and sets the icon badge — reaches a phone
    // that already installed the app essentially never. That is exactly how
    // the badge kept working in-app while doing nothing on the home screen.
    void registration.update().catch(() => {
      /* offline, or the check raced a navigation; next launch retries */
    });

    return registration;
  } catch (error) {
    console.warn('[push] service worker registration failed', error);
    return null;
  }
}

/**
 * Ask for permission and register a subscription.
 *
 * Must be called from a user gesture — both Safari and Chrome reject a
 * permission request that is not, which is why there is a button for this
 * rather than an automatic prompt on first load.
 */
export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  const status = pushStatus();
  if (status === 'needs-install') {
    return { ok: false, reason: 'Add The Huddle to your home screen first.' };
  }
  if (status === 'unsupported') {
    return { ok: false, reason: 'This browser cannot do notifications.' };
  }
  if (status === 'denied') {
    return { ok: false, reason: 'Notifications are blocked in your browser settings.' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'Notifications were not allowed.' };

  const registration = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration;
  const { key } = await get<{ key: string }>('/push/key');

  const subscription = await registration.pushManager.subscribe({
    // Non-negotiable for Chrome: it refuses silent push outright.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });

  const json = subscription.toJSON();
  await post('/push/subscribe', {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });

  return { ok: true };
}

export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  await post('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {});
  await subscription.unsubscribe();
}

/**
 * VAPID keys travel as base64url; PushManager wants raw bytes.
 *
 * Backed by an explicitly-constructed ArrayBuffer so the result is
 * `Uint8Array<ArrayBuffer>` rather than `Uint8Array<ArrayBufferLike>` — the
 * latter admits SharedArrayBuffer, which BufferSource does not accept.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * The app icon's unread badge.
 *
 * Only Chromium implements the Badging API today; on iOS the badge comes from
 * the push notification itself. Guarded rather than polyfilled — a missing
 * badge is a non-event, a thrown error on every unread change is not.
 */
export function setAppBadge(count: number): void {
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0) void nav.setAppBadge?.(count);
    else void nav.clearAppBadge?.();
  } catch {
    /* badging is decoration; never let it break a render */
  }
}
